import { createTheme } from '@mui/material/styles';

// Centralized color palette based on globalColors
export const palette = {
  primary: {
    main: '#2EA1F8',
    light: '#EDF7FF',
    dark: '#1E7BC7',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#354052',
    light: '#7F8FA4',
    dark: '#1A1F29',
    contrastText: '#ffffff',
  },
  error: {
    main: '#FF4081',
    light: '#FF6B9D',
    dark: '#E91E63',
    contrastText: '#ffffff',
  },
  warning: {
    main: '#FFAB2B',
    light: '#FFD166',
    dark: '#F57C00',
    contrastText: '#000000',
  },
  success: {
    main: '#1BB934',
    light: '#4CAF50',
    dark: '#087F23',
    contrastText: '#ffffff',
  },
  grey: {
    50: '#F2F5FC',
    100: '#EFF3F6',
    200: '#E6E9ED',
    300: '#C5D0DE',
    400: '#7F8FA4',
    500: '#354052',
    600: '#2A3441',
    700: '#1A1F29',
    800: '#0F1419',
    900: '#000000',
  },
  background: {
    default: '#F2F5FC',
    paper: '#FFFFFF',
    card: '#FFFFFF',
    disabled: '#C5D0DE',
  },
  text: {
    primary: '#354052',
    secondary: '#7F8FA4',
    disabled: '#C5D0DE',
  },
};

// Typography configuration
export const typography = {
  fontFamily: 'Rubik, Helvetica, sans-serif',
  h1: {
    fontSize: '32px',
    fontWeight: 700,
    lineHeight: '38px',
  },
  h2: {
    fontSize: '24px',
    fontWeight: 700,
    lineHeight: '28px',
  },
  h3: {
    fontSize: '20px',
    fontWeight: 500,
    lineHeight: '24px',
  },
  h4: {
    fontSize: '18px',
    fontWeight: 500,
    lineHeight: '22px',
  },
  h5: {
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '20px',
  },
  h6: {
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '18px',
  },
  body1: {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
  },
  body2: {
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: '16px',
  },
  button: {
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '16px',
    textTransform: 'none' as const,
  },
  caption: {
    fontSize: '11px',
    fontWeight: 400,
    lineHeight: '14px',
  },
};

// Common spacing and sizing
export const spacing = {
  xs: '4px',
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '20px',
  xxl: '24px',
};

// Common animation styles for reuse
export const animations = {
  appearanceEffect: {
    '@keyframes appearanceEffect': {
      '0%': {
        opacity: 0,
        transform: 'translateY(10px)',
      },
      '100%': {
        opacity: 1,
        transform: 'translateY(0)',
      },
    },
    animation: 'appearanceEffect 500ms ease-out',
  },
  fadeInEffect: {
    '@keyframes fadeInEffect': {
      '0%': {
        opacity: 0,
      },
      '100%': {
        opacity: 1,
      },
    },
    animation: 'fadeInEffect 300ms ease-out',
  },
};

// Common layout styles
export const layouts = {
  reportWrapper: {
    display: 'flex',
    flexDirection: 'column',
    padding: '14px',
    gap: '16px',
    backgroundColor: palette.background.default,
    width: '100%',
    paddingBottom: '60px',
    overflow: 'overlay',
    '&::-webkit-scrollbar': {
      width: '9px',
    },
    '&::-webkit-scrollbar-track': {
      borderLeft: 0,
    },
    '&::-webkit-scrollbar-thumb': {
      background: palette.grey[300],
      borderRadius: '13px',
    },
    ...animations.appearanceEffect,
  },
  cardWrapper: {
    display: 'flex',
    flexDirection: 'column',
    padding: '1%',
    border: `1px solid ${palette.grey[200]}`,
    borderRadius: '8px',
    background: palette.background.paper,
    ...animations.appearanceEffect,
  },
  flexRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  flexColumn: {
    display: 'flex',
    flexDirection: 'column',
  },
};

// Create MUI theme
const theme = createTheme({
  palette,
  typography,
  spacing: 8,
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: '4px',
          padding: '8px 16px',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: `1px solid ${palette.grey[200]}`,
          boxShadow: '0px 0px 3px rgba(0, 0, 0, 0.03)',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: palette.background.paper,
          color: palette.text.primary,
          fontSize: '12px',
          border: `1px solid ${palette.grey[200]}`,
          boxShadow: '0 0 2px rgba(0,0,0,0.5)',
        },
        arrow: {
          color: palette.background.paper,
        },
      },
    },
  },
});

export default theme;
